import React, { useState } from 'react';
import { Container, Nav } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'react-bootstrap-icons';
import Footer from '../components/Footer';
import './Terms.css';

const Terms: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'legal' | 'consumer' | 'commercial'>('legal');
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="terms-page terms-page-no-sidebar">
      <Container className="terms-container">
        <button onClick={handleGoBack} className="back-link">
          <ArrowLeft className="me-2" />
          Go Back
        </button>

        <h1 className="terms-title">terms & privacy</h1>

        <Nav variant="tabs" className="terms-nav mb-4">
          <Nav.Item>
            <Nav.Link
              active={activeTab === 'legal'}
              onClick={() => setActiveTab('legal')}
              className="terms-tab"
            >
              LEGAL & COMPLIANCE
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeTab === 'consumer'}
              onClick={() => setActiveTab('consumer')}
              className="terms-tab"
            >
              CONSUMER TERMS OF SERVICE
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeTab === 'commercial'}
              onClick={() => setActiveTab('commercial')}
              className="terms-tab"
            >
              COMMERCIAL TERMS OF SERVICE
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {activeTab === 'legal' && (
          <div className="terms-content">
            <p className="terms-intro">
              Legal agreements, security, and data handling information for the <strong>Revealer ai Platform</strong>.
            </p>

            <section className="terms-section">
              <h2 className="section-title">Terms & Conditions Links</h2>
              <p className="section-description">
                Your use of the <strong>Revealer ai Platform</strong> (including all Starter, Pro, and Ultra subscription plans) is governed by the specific terms that apply to your user type.
              </p>

              <table className="terms-table">
                <thead>
                  <tr>
                    <th>User Type</th>
                    <th>Applicable Terms</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>Commercial Users</strong> (Individuals or entities accessing the Services for business, enterprise, or professional purposes)
                    </td>
                    <td>
                      <a href="#commercial" onClick={(e) => { e.preventDefault(); setActiveTab('commercial'); }}>
                        Commercial Terms
                      </a>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Consumer Users</strong> (Individuals accessing the Services solely for personal, non-commercial use)
                    </td>
                    <td>
                      <a href="#consumer" onClick={(e) => { e.preventDefault(); setActiveTab('consumer'); }}>
                        Consumer Terms
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>

            <section className="terms-section">
              <h2 className="section-title">Security, Data, and Foundational Trust</h2>

              <div className="subsection">
                <h3 className="subsection-title">C.1. Data Processing and Confidentiality</h3>
                <p className="subsection-text">
                  Data submitted through the Services will be processed in accordance with Revealer ai's Data Processing Addendum (DPA), which is incorporated
                  into these Terms by reference. Both parties will treat all Customer Content (Inputs and Outputs) as the Customer's Confidential Information as
                  defined in Section E (Confidentiality).
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">C.2. Platform Security Controls</h3>
                <p className="subsection-text">
                  Revealer ai will maintain industry-standard administrative, physical, and technical safeguards designed to protect the security, confidentiality, and
                  integrity of Customer Content within the Revealer ai Platform. These safeguards include, but are not limited to, encryption of data in transit and at
                  rest, role-based access control, and continuous vulnerability management.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">C.3. AI Wrapper and Foundational Model Reliance</h3>
                <p className="subsection-text">
                  Revealer ai incorporates the following security and trust features related to the platform's AI architecture:
                </p>

                <table className="terms-table nested-table">
                  <thead>
                    <tr>
                      <th>Feature Type</th>
                      <th>Assistance Provided by Revealer AI</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Direct Feature</strong> (Implemented by <strong>Revealer ai</strong>)
                      </td>
                      <td>
                        <p><strong>Strict Data Governance</strong>: We utilize contractual and technical controls to ensure your Customer Content is
                        processed in a secure, isolated environment and is <strong>never retained for foundational model training or enhancement</strong> beyond
                        the necessary processing period.</p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Inherited Feature</strong> (From Foundational Model Provider)
                      </td>
                      <td>
                        <p><strong>Leveraged Infrastructure Trust</strong>: The integrity of the <strong>Revealer ai Platform</strong> is underpinned by an <strong>industry-leading
                        foundational model provider</strong> whose infrastructure is <strong>SOC 2 Type II certified</strong>. This provides a secure and independently audited environment
                        for the processing of all Customer Content.</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">C.4. Shared Responsibility for AI Systems</h3>
                <p className="subsection-text">
                  Customer acknowledges that the security and efficacy of the service is governed by a <strong>Shared Responsibility Model</strong>. While <strong>Revealer ai</strong> is
                  responsible for the security of the <strong>Revealer ai Platform</strong> and its controls over data in transit and at rest, the Customer is <strong>solely responsible</strong> for:
                </p>
                <ul className="responsibility-list">
                  <li><strong>Input Screening</strong>: Ensuring all Inputs do not contain high-risk data (e.g., PHI, PII) beyond what is explicitly necessary for the intended use and compliant with all relevant laws.</li>
                  <li><strong>User Access</strong>: Managing access, credentials, and permissions for all User accounts.</li>
                  <li><strong>Output Validation</strong>: Reviewing and validating all Outputs for accuracy, completeness, and appropriateness for their intended final use, as acknowledged in Section D.3 (Limitations of Outputs).</li>
                </ul>
              </div>
            </section>

            <section className="terms-section">
              <h2 className="section-title">Security, Data, and Foundational Trust</h2>
              <p className="subsection-text">
                <strong>Revealer ai</strong> is committed to securing our platform. If you believe you have discovered a security vulnerability in the <strong>Revealer ai Platform</strong>, please
                use our designated reporting channel to disclose the issue responsibly.
              </p>
              <p className="subsection-text">
                Please email: <a href="mailto:info@revealer.ai" className="email-link">info@revealer.ai</a>
              </p>
            </section>
          </div>
        )}

        {activeTab === 'consumer' && (
          <div className="terms-content">
            <div className="terms-note">
              <strong>Note:</strong> This policy is effective on the date you acknowledge these updated Terms within our Services, or October 3, 2025, whichever is earlier.
            </div>

            <p className="subsection-text">
              Welcome to <strong>Revealer ai</strong>. Before accessing our services, please read these Terms of Service.
            </p>

            <p className="subsection-text">
              These Terms of Service ("<strong>Terms</strong>") govern your use of <strong>Revealer ai</strong> and all other related products and services that we may offer for
              individual consumers, along with any associated apps, software, and websites (collectively, "<strong>Services</strong>"). This document forms a contract
              between you and <strong>Revealer LLC</strong> ("<strong>Revealer</strong>"), and they include our <strong>Acceptable Use Policy</strong>. By accessing our Services, you agree to these
              Terms.
            </p>

            <p className="subsection-text">
              Please read our <strong>Privacy Policy</strong>, which describes how we collect and use your personal information.
            </p>

            <p className="subsection-text">
              <strong>Please note:</strong> Our <strong>Commercial Terms of Service</strong> govern the use of the <strong>Revealer</strong> business platform or any other <strong>Revealer</strong> offerings that
              reference the Commercial Terms of Service. This document does not apply to that use.
            </p>

            <p className="subsection-text">
              <strong>Effective Date:</strong> October 3, 2025
            </p>

            <section className="terms-section">
              <h2 className="section-title">1. General Terms and Use</h2>

              <div className="subsection">
                <h3 className="subsection-title">Who We Are and What We Do</h3>
                <p className="subsection-text">
                  Revealer LLC is an AI-powered UX design and intelligence platform dedicated to accelerating research and design workflows for individuals.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">Minimum Age</h3>
                <p className="subsection-text">
                  You must be at least 16 years old or the minimum age required to consent to use the Services in your location, whichever is higher.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">Your Revealer Account</h3>
                <p className="subsection-text">
                  To access our Services, we may require you to create an Account. You are responsible for all activity under your Account and agree to notify us
                  immediately of any unauthorized access by emailing us at <a href="mailto:info@revealer.ai" className="email-link">info@revealer.ai</a>.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">Permitted Use</h3>
                <p className="subsection-text">
                  You may access and use our Services only in compliance with these Terms, including our <strong>Acceptable Use Policy</strong> and any guidelines we may post.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">Restricted Use</h3>
                <p className="subsection-text">
                  You may not access or use, or help another person to access or use, our Services to:
                </p>
                <ul className="responsibility-list">
                  <li>Violate any applicable laws or regulations.</li>
                  <li>Develop any products or services that compete with our Services, including to develop or train any artificial intelligence or machine learning algorithms or models.</li>
                  <li>Reverse-engineer, disassemble, or otherwise reduce our Services or the underlying foundational models to human-readable form.</li>
                  <li>Abuse, harm, interfere with, or disrupt our Services (e.g., introducing viruses or malware, spamming, or bypassing protective measures).</li>
                </ul>
              </div>
            </section>

            <section className="terms-section">
              <h2 className="section-title">2. Inputs, Outputs, and Materials</h2>

              <div className="subsection">
                <h3 className="subsection-title">Generally</h3>
                <p className="subsection-text">
                  You may interact with our Services by providing information ("<strong>Inputs</strong>"). Our Services will generate responses ("<strong>Outputs</strong>"). Inputs and Outputs
                  collectively are "<strong>Materials</strong>."
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">Your Ownership of Content</h3>
                <p className="subsection-text">
                  You retain <strong>full ownership</strong> of all designs and artifacts you create using the <strong>Revealer ai Platform</strong> ("<strong>Your Content</strong>") and may use Your Content for
                  commercial purposes.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">License to Revealer ai</h3>
                <p className="subsection-text">
                  You grant <strong>Revealer ai</strong> a limited, worldwide license to Your Content solely to operate, improve, and optimize the Services. You agree that <strong>Revealer
                  ai</strong> may utilize your anonymized or aggregated Input and Output data to enhance the Services, unless you opt out through your account settings.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">Reliance on Outputs</h3>
                <p className="subsection-text">
                  Artificial intelligence and machine learning technology is still improving. When you use our Services, you acknowledge and agree:
                </p>
                <ul className="responsibility-list">
                  <li><strong>Outputs are not guaranteed to be accurate, complete, or free of error.</strong></li>
                  <li>You should not rely on any Outputs without independently confirming their accuracy.</li>
                  <li><strong>The Service is provided "AS IS" without any warranties.</strong></li>
                </ul>
              </div>
            </section>

            <section className="terms-section">
              <h2 className="section-title">3. Subscriptions, Fees, and Payment</h2>

              <div className="subsection">
                <h3 className="subsection-title">Subscription Plans</h3>
                <p className="subsection-text">
                  Access to the <strong>Revealer ai Platform</strong> is provided via three subscription tiers, each with specific usage limitations ("messages" or generations) and
                  features:
                </p>

                <table className="terms-table">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Monthly Fee</th>
                      <th>Usage Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Starter Plan</strong></td>
                      <td>$0/month</td>
                      <td>TBD</td>
                    </tr>
                    <tr>
                      <td><strong>Pro Plan</strong></td>
                      <td>$49/month</td>
                      <td>TBD</td>
                    </tr>
                    <tr>
                      <td><strong>Ultra Plan</strong></td>
                      <td>$149/month</td>
                      <td>TBD</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">Billing and Cancellation Policy</h3>
                <p className="subsection-text">
                  <strong>Billing Cycle:</strong> All subscription fees are charged monthly on your <strong>billing renewal date</strong>. You are responsible for the fees for the entire month (the full
                  billing cycle) of the plan you are currently using.
                </p>
                <p className="subsection-text">
                  <strong>Cancellation:</strong> You can cancel your monthly subscription at any time to prevent future charges. To avoid being charged for the next month, you
                  must cancel before your billing renewal date.
                </p>
                <p className="subsection-text">
                  <strong>Refunds:</strong> Monthly subscription fees are <strong>non-refundable</strong>. If you cancel, your access to <strong>Revealer ai</strong> will continue until the end of your current paid
                  billing cycle, at which point the cancellation will take effect. All annual subscriptions are <strong>non-refundable</strong>.
                </p>
              </div>
            </section>

            <section className="terms-section">
              <h2 className="section-title">4. Legal & Liability</h2>

              <div className="subsection">
                <h3 className="subsection-title">E.2 Limitation of Liability and Warranty</h3>
                <p className="subsection-text">
                  <strong>(A) DISCLAIMER OF WARRANTIES. THE REVEALER AI PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE." REVEALER AI
                  DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED,</strong> including, but not limited to, any implied warranties of merchantability, fitness for a
                  particular purpose, or non-infringement.
                </p>
                <p className="subsection-text">
                  <strong>(B) EXCLUSION OF DAMAGES. YOU EXPRESSLY UNDERSTAND AND AGREE THAT REVEALER AI SHALL NOT BE LIABLE TO YOU FOR
                  ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES,</strong> including, but not limited to, damages for loss of
                  profits, data, or other intangible losses.
                </p>
                <p className="subsection-text">
                  <strong>(C) LIABILITY CAP. IN NO EVENT SHALL REVEALER AI'S TOTAL CUMULATIVE LIABILITY TO YOU EXCEED THE GREATER OF (i) ONE
                  HUNDRED U.S. DOLLARS ($100.00) or (ii) THE AMOUNT YOU HAVE PAID TO REVEALER AI IN THE SIX (6) MONTHS PRECEDING THE
                  CLAIM.</strong>
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">E.2. Governing Law and Dispute Resolution</h3>
                <p className="subsection-text">
                  <strong>(A) GOVERNING LAW:</strong> This Agreement and any disputes arising out of or related to it shall be governed by the laws of the <strong>State of Texas</strong>, without
                  regard to its conflict of law provisions.
                </p>
                <p className="subsection-text">
                  <strong>(B) MANDATORY BINDING ARBITRATION:</strong> Any dispute, controversy, or claim arising out of or relating to this Agreement shall be settled by
                  <strong>mandatory and binding arbitration in Houston, Texas</strong>, before a single arbitrator. The arbitration shall be administered by JAMS pursuant to its
                  Comprehensive Arbitration Rules and Procedures.
                </p>
                <p className="subsection-text">
                  <strong>(C) WAIVER OF CLASS ACTION:</strong> You and <strong>Revealer ai</strong> agree that each may bring claims against the other only in your or its individual capacity
                  and not as a plaintiff or class member in any purported class or representative proceeding.
                </p>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'commercial' && (
          <div className="terms-content">
            <p className="subsection-text">
              Welcome to <strong>Revealer</strong>! Please read these Commercial Terms of Service ("<strong>Terms</strong>") carefully before accessing our Platform.
            </p>

            <p className="subsection-text">
              These Terms constitute a legal agreement between <strong>Revealer LLC</strong> ("<strong>Revealer</strong>") and you or the organization, company, or entity you
              represent ("<strong>Customer</strong>"). They govern your use of the <strong>Revealer ai</strong> UX design and intelligence platform and all related tools, documentation,
              and services (the "<strong>Platform</strong>" or "<strong>Services</strong>"). These Terms are effective on the earlier of the date you first electronically agree to a version of
              these Terms or the date you first access the Services ("<strong>Effective Date</strong>").
            </p>

            <div className="terms-note">
              <strong>Important Note:</strong> You must have the legal authority to bind your organization to these Terms. The Services provided under these Terms are
              <strong>for business or professional use only, not for consumer use.</strong>
            </div>

            <p className="subsection-text">
              <strong>Effective Date:</strong> October 3, 2025
            </p>

            <section className="terms-section">
              <h2 className="section-title">A. General Terms</h2>
              <p className="subsection-text">
                These Commercial Terms govern the use of the Revealer ai Platform by any individual or entity using the services for business, enterprise, or
                professional purposes.
              </p>
            </section>

            <section className="terms-section">
              <h2 className="section-title">B. Business Model, Access, and Payments</h2>
              <p className="subsection-text">
                These Commercial Terms govern the use of the Revealer ai Platform by any individual or entity using the services for business, enterprise, or
                professional purposes.
              </p>

              <div className="subsection">
                <h3 className="subsection-title">B.1. Subscription Plans and Usage Limits</h3>
                <p className="subsection-text">
                  Access to the Revealer ai Platform is provided via three distinct subscription tiers, each with specific usage limitations ("messages" or generations)
                  and features:
                </p>

                <table className="terms-table">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Monthly Fee</th>
                      <th>Usage Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Starter Plan</strong></td>
                      <td>$0/month</td>
                      <td>TBD</td>
                    </tr>
                    <tr>
                      <td><strong>Pro Plan</strong></td>
                      <td>$49/month</td>
                      <td>TBD</td>
                    </tr>
                    <tr>
                      <td><strong>Ultra Plan</strong></td>
                      <td>$149/month</td>
                      <td>TBD</td>
                    </tr>
                  </tbody>
                </table>

                <p className="subsection-text">
                  The Company reserves the right to suspend or terminate access if Customer exceeds the stipulated usage limits without upgrading their plan or
                  purchasing additional usage.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">B.2. Billing and Cancellation</h3>
                <p className="subsection-text">
                  <strong>Billing Cycle:</strong> All subscription fees are charged monthly on your <strong>billing renewal date</strong>. You are responsible for the fees for the entire month (the full
                  billing cycle) of the plan you are currently using.
                </p>
                <p className="subsection-text">
                  <strong>Cancellation:</strong> You can cancel your monthly subscription at any time to prevent future charges. To avoid being charged for the next month, you
                  must cancel before your next <strong>billing renewal date</strong>. Your renewal data is always available on your profile page.
                </p>
                <p className="subsection-text">
                  <strong>Refunds:</strong> Monthly subscription fees are <strong>non-refundable</strong>. If you cancel, your access to <strong>Revealer ai</strong> will continue until the end of your current paid
                  billing cycle, at which point the cancellation will take effect. All annual subscriptions are <strong>non-refundable</strong> (as previously discussed, though this
                  needs to be explicitly in the final commercial terms).
                </p>
              </div>
            </section>

            <section className="terms-section">
              <h2 className="section-title">C. Intellectual Property (IP) and Content Ownership</h2>

              <div className="subsection">
                <h3 className="subsection-title">C.1. Customer Content Ownership and Commercial Rights</h3>
                <p className="subsection-text">
                  The Customer retains <strong>full ownership</strong> of all Inputs provided to the <strong>Revealer ai Platform</strong> and all resulting Outputs and generated artifacts
                  (collectively, "<strong>Customer Content</strong>"). There are <strong>no restrictions</strong> on the Customer's right to use, sell, or commercialize any Customer Content.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">C.2. License Granted to Revealer ai</h3>
                <p className="subsection-text">
                  The Customer grants Revealer ai a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and analyze the Customer Content
                  solely for the purpose of operating, improving, and optimizing the Services. This license includes the right to create <strong>anonymized or aggregated
                  Customer Content</strong> for the purpose of general platform improvements.
                </p>
              </div>
            </section>

            <section className="terms-section">
              <h2 className="section-title">D. AI-Specific Disclaimers and Risk</h2>

              <div className="subsection">
                <h3 className="subsection-title">D.1. Accuracy and Limitation of Outputs</h3>
                <p className="subsection-text">
                  <strong>REVEALER AI</strong> uses sophisticated artificial intelligence which can, at times, generate incorrect, incomplete, or offensive content ("<strong>Outputs</strong>"). The
                  Customer acknowledges and agrees that Outputs are <strong>not guaranteed to be accurate or complete</strong>. The Customer is <strong>solely responsible</strong> for
                  reviewing, validating, and ensuring the accuracy and appropriateness of all Outputs before use or dissemination, including any content intended for
                  commercial or legal purposes.
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">D.2. Prohibited Uses</h3>
                <p className="subsection-text">
                  The Customer shall not use the <strong>Revealer ai Platform</strong> to create, generate, or disseminate any content that is illegal, defamatory, harmful, infringes
                  on any third-party rights, or is otherwise prohibited by applicable law. <strong>Revealer ai</strong> implements preventative protections, but compliance and legal
                  risk ultimately remain the <strong>Customer's sole responsibility</strong>.
                </p>
              </div>
            </section>

            <section className="terms-section">
              <h2 className="section-title">E. Legal & Liability</h2>

              <div className="subsection">
                <h3 className="subsection-title">E.1. Limitation of Liability</h3>
                <p className="subsection-text">
                  <strong>(A) EXCLUSION OF DAMAGES. EXCEPT FOR LIABILITY ARISING FROM REVEALER AI'S INDEMNIFICATION OBLIGATIONS OR
                  CUSTOMER'S BREACH OF SECTION C (INTELLECTUAL PROPERTY) OR SECTION D.2 (PROHIBITED USES), NEITHER PARTY
                  NOR ITS AFFILIATES SHALL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE, CONSEQUENTIAL, OR EXEMPLARY
                  DAMAGES,</strong> including, but not limited to, damages for loss of profits, goodwill, use, data, or other intangible losses (even if such party
                  has been advised of the possibility of such damages).
                </p>
                <p className="subsection-text">
                  <strong>(B) LIABILITY CAP. IN NO EVENT SHALL THE AGGREGATE LIABILITY OF REVEALER AI AND ITS AFFILIATES ARISING OUT OF OR
                  RELATED TO THIS AGREEMENT, WHETHER IN CONTRACT, TORT, OR OTHERWISE, EXCEED THE TOTAL AMOUNT OF FEES PAID
                  BY THE CUSTOMER TO REVEALER AI UNDER THIS AGREEMENT DURING THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING
                  THE EVENT GIVING RISE TO THE CLAIM.</strong>
                </p>
              </div>

              <div className="subsection">
                <h3 className="subsection-title">E.2. Governing Law and Dispute Resolution</h3>
                <p className="subsection-text">
                  <strong>(A) GOVERNING LAW:</strong> This Agreement and any disputes arising out of or related to it shall be governed by the laws of the <strong>State of Texas</strong>, without
                  regard to its conflict of law rules.
                </p>
                <p className="subsection-text">
                  <strong>(B) MANDATORY BINDING ARBITRATION:</strong> Any dispute, controversy, or claim arising out of
                  or relating to this Agreement shall be settled by <strong>mandatory and binding arbitration in Houston, Texas</strong>, before a single arbitrator. The arbitration
                  shall be administered by JAMS pursuant to its Comprehensive Arbitration Rules and Procedures.
                </p>
                <p className="subsection-text">
                  <strong>(C) WAIVER OF CLASS ACTION:</strong> You and <strong>Revealer ai</strong> agree that each may bring claims against the other only in your or its individual capacity
                  and not as a plaintiff or class member in any purported class or representative proceeding.
                </p>
              </div>
            </section>
          </div>
        )}
      </Container>
      <Footer />
    </div>
  );
};

export default Terms;
